import { cva, type VariantProps } from "class-variance-authority";
import "../Input/input.css";
import {
  forwardRef,
  useState,
  useRef,
  type ComponentProps,
  type ReactNode,
} from "react";
import type { IconType } from "react-icons";
import { assets } from "@core/assets";
import { cn } from "@core/utils/cn";
import Image from "../Image";

/* ==================== INPUT FIELD ==================== */
const inputStyles = cva(
  [
    "relative",
    "py-1.5",
    "px-2.5",
    "ease-in-out",
    "duration-200",
    "transition-all",
    "w-full",
    "appearance-none",
    "rounded-lg",
    "outline-none",
    // Keyboard focus ring — always accent-blue
    "focus-visible:ring-0",
    "focus-visible:border-primary-800",
    // Disabled — identical across all variants
    "disabled:cursor-not-allowed",
    "disabled:bg-neutral-50",
    "disabled:text-neutral-300",
    "disabled:placeholder:text-neutral-300",
    "disabled:border-neutral-200",
  ],
  {
    variants: {
      variant: {
        // ── Primary ──────────────────────────────────────────────
        // Default:  bg-neutral-50  border-neutral-200
        // Hover:    bg-neutral-100 border-neutral-200
        // Error:    bg-neutral-50  border-accent-red-800
        primary: [
          "border-[1px]",
          "placeholder:text-neutral-300",
          "hover:bg-neutral-50",
          "hover:border-neutral-200",
        ],
      },
      proportions: {
        lg: "h-[2.375rem]",
        sm: "h-[1.813rem]",
      },
      state: {
        // Default text: neutral-950 (empty), filled drives its own text color via `isFilled`
        default: ["border-neutral-200", "text-neutral-950"],
        error: [
          "border-accent-red-800",
          "bg-neutral-50",
          // Focus keeps border red
          "focus-visible:border-accent-red-800",
        ],
        info: [
          "border-primary-800",
          "text-primary-800",
          "placeholder:text-primary-800",
          "focus-visible:border-primary-800",
        ],
        warning: [
          "border-accent-yellow-700",
          "text-accent-yellow-800",
          "placeholder:text-accent-yellow-400",
          "focus-visible:border-accent-yellow-700",
        ],
        success: [
          "border-accent-green-700",
          "text-accent-green-700",
          "placeholder:text-accent-green-400",
          "focus-visible:border-accent-green-700",
        ],
      },
      // Filled: user has typed something — text becomes neutral-950 (full opacity, prominent)
      // Empty:  text-neutral-400 (lighter, placeholder-like weight)
      isFilled: {
        true: "text-neutral-950 font-normal bg-transparent",
        false: "text-neutral-950 bg-transparent",
      },
      inputType: {
        text: "",
        number:
          "[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
        date: "[&::-webkit-calendar-picker-indicator]:opacity-70 [&::-webkit-calendar-picker-indicator]:cursor-pointer",
      },
    },
    // When filled + error, error text color wins
    compoundVariants: [
      {
        state: "info",
        isFilled: true,
        className: "text-primary-800",
      },
      {
        state: "warning",
        isFilled: true,
        className: "text-accent-yellow-800",
      },
      {
        state: "success",
        isFilled: true,
        className: "text-accent-green-700",
      },
    ],
    defaultVariants: {
      variant: "primary",
      proportions: "lg",
      state: "default",
      isFilled: false,
      inputType: "text",
    },
  },
);

/* ==================== WRAPPER ==================== */
const wrapperVariants = cva("relative w-full", {
  variants: {
    wrapperVariant: {
      default: "flex flex-col",
      flexed: "flex items-center gap-3",
    },
  },
  defaultVariants: {
    wrapperVariant: "default",
  },
});

/* ==================== LABEL ==================== */
const labelStyles = cva(
  [
    "block",
    "mb-1",
    "text-sm",
    "font-medium",
    "transition-colors",
    "duration-200",
    "ease-in-out",
    "select-none",
  ],
  {
    variants: {
      state: {
        default: "text-neutral-950",
        error: "text-accent-red-800",
        focused: "text-primary-800",
      },
      required: {
        true: "after:content-['*'] after:ml-1 after:text-accent-red-800",
      },
    },
    defaultVariants: {
      state: "default",
    },
  },
);

/* ==================== HELPER TEXT ==================== */
const helperTextStyles = cva(
  ["flex", "mt-2", "text-sm", "gap-2", "items-center"],
  {
    variants: {
      state: {
        default: "text-neutral-950",
        info: "text-primary-800",
        error: "text-accent-red-600",
        warning: "text-accent-yellow-700",
        success: "text-accent-green-700",
      },
    },
    defaultVariants: {
      state: "default",
    },
  },
);

/* ==================== ICON BUTTON (eye / copy) ==================== */
const iconButtonStyles = [
  "cursor-pointer",
  "rounded",
  "transition-all",
  "duration-150",
  "outline-none",
  "p-0.5",
  "-m-0.5",
  "active:scale-90",
  "focus-visible:ring-2",
  "focus-visible:ring-offset-1",
  "focus-visible:ring-primary-800",
].join(" ");

/* ==================== TYPES ==================== */
type InputProps = ComponentProps<"input"> &
  VariantProps<typeof inputStyles> &
  VariantProps<typeof wrapperVariants> & {
    type?: "text" | "number" | "date" | "email" | "password" | "file";
    leftIcon?: IconType | string | ReactNode;
    rightIcon?: IconType | string | ReactNode;
    label?: string;
    required?: boolean;
    helperText?: string;
    copy?: boolean;
  };

/* ==================== COMPONENT ==================== */
const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      variant,
      proportions,
      wrapperVariant,
      state = "default",
      className,
      type = "text",
      rightIcon: RightIcon,
      leftIcon: LeftIcon,
      label,
      required = false,
      helperText,
      copy = false,
      id,
      ...props
    },
    ref,
  ) => {
    const helperId = id ? `${id}-helper` : undefined;
    const internalRef = useRef<HTMLInputElement>(null);
    const combinedRef = (node: HTMLInputElement) => {
      internalRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref)
        (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
    };

    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [isFilled, setIsFilled] = useState(
      // initialise from a controlled or defaultValue if provided
      !!(props.value ?? props.defaultValue),
    );
    const [copied, setCopied] = useState(false);

    const EyeIcon = assets["eye"];
    const CloseEyeIcon = assets["eye-off"];
    const CopyIcon = assets["copy"];
    const CheckIcon = assets["check"];

    const inputTypeVariant =
      type === "number" ? "number" : type === "date" ? "date" : "text";

    const togglePasswordVisibility = () => setIsPasswordVisible((p) => !p);

    const handleCopy = async () => {
      if (internalRef.current) {
        await navigator.clipboard.writeText(internalRef.current.value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }
    };

    // Icon color tracks state → focused → resting, using the real palette
    const getIconColor = () => {
      if (state === "error") return "text-accent-red-800";
      if (state === "success") return "text-accent-green-700";
      if (state === "warning") return "text-accent-yellow-800";
      if (state === "info") return "text-primary-800";
      if (isFocused) return "text-primary-800";
      if (isFilled) return "text-neutral-950";
      return "text-neutral-300";
    };

    const getLabelState = () => {
      if (state === "error") return "error";
      if (isFocused) return "focused";
      return "default";
    };

    const getStateIcon = () => {
      switch (state) {
        case "info":
          return assets["circle-alert"];
        case "success":
          return assets["circle-check-big"];
        case "warning":
          return assets["triangle-alert"];
        case "error":
          return assets["circle-x"];
        default:
          return assets["circle-alert"];
      }
    };

    const StateIcon = getStateIcon();
    const helperState =
      state === "default"
        ? "default"
        : (state as "info" | "error" | "warning" | "success");

    return (
      <div className={cn(wrapperVariants({ wrapperVariant }))}>
        {label && (
          <h4
            className={cn(
              labelStyles({ state: getLabelState(), required }),
              "flex items-center",
              wrapperVariant === "flexed" ? "min-w-[160px]" : "",
            )}
          >
            <span className="text-sm">{label}</span>
          </h4>
        )}

        <div className="relative w-full flex items-center">
          {/* ── Left icon ── */}
          {LeftIcon && (
            <div
              className={cn(
                "absolute left-2 z-30 pointer-events-none transition-colors duration-200 ease-in-out",
                getIconColor(),
              )}
            >
              {typeof LeftIcon === "string" ? (
                <Image src={LeftIcon} />
              ) : typeof LeftIcon === "function" ? (
                <LeftIcon className={cn("text-xl", getIconColor())} />
              ) : (
                LeftIcon
              )}
            </div>
          )}

          {/* ── Input ── */}
          <input
            ref={combinedRef}
            id={id}
            aria-describedby={helperText && helperId ? helperId : undefined}
            type={
              type === "password"
                ? isPasswordVisible
                  ? "text"
                  : "password"
                : type
            }
            className={cn(
              inputStyles({
                variant,
                proportions,
                state,
                isFilled,
                inputType: inputTypeVariant,
              }),
              LeftIcon ? "pl-8" : "",
              RightIcon || type === "password" || copy ? "pr-10" : "",
              className,
            )}
            onFocus={(e) => {
              setIsFocused(true);
              props.onFocus?.(e);
            }}
            onBlur={(e) => {
              setIsFocused(false);
              props.onBlur?.(e);
            }}
            onChange={(e) => {
              setIsFilled(e.target.value.length > 0);
              props.onChange?.(e);
            }}
            {...(type === "number" && {
              step: props.step || "any",
              inputMode: "numeric",
            })}
            {...props}
          />

          {/* ── Right icons ── */}
          {(RightIcon || type === "password" || copy) && (
            <div className="absolute right-2 z-30 flex items-center gap-1">
              {type === "password" ? (
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className={cn(iconButtonStyles, getIconColor())}
                  aria-label={
                    isPasswordVisible ? "Hide password" : "Show password"
                  }
                  tabIndex={0}
                >
                  {isPasswordVisible ? (
                    <CloseEyeIcon className="text-xl" />
                  ) : (
                    <EyeIcon className="text-xl" />
                  )}
                </button>
              ) : RightIcon ? (
                typeof RightIcon === "string" ? (
                  <Image src={RightIcon} />
                ) : typeof RightIcon === "function" ? (
                  <RightIcon className={cn("text-xl", getIconColor())} />
                ) : (
                  RightIcon
                )
              ) : null}

              {copy && (
                <button
                  type="button"
                  onClick={handleCopy}
                  className={cn(
                    iconButtonStyles,
                    copied ? "text-accent-green-600" : getIconColor(),
                  )}
                  aria-label={copied ? "Copied!" : "Copy to clipboard"}
                  tabIndex={0}
                >
                  {copied ? (
                    <CheckIcon className="text-xl" />
                  ) : (
                    <CopyIcon className="text-xl text-neutral-500" />
                  )}
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── Helper text ── */}
        {helperText && (
          <div className={cn(helperTextStyles({ state: helperState }))}>
            <StateIcon className="text-lg flex-shrink-0" />
            <span id={helperId}>{helperText}</span>
          </div>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";
export default Input;
